import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where
} from "firebase/firestore";
import { TrashIcon } from "@heroicons/react/24/outline";
import { db } from "../../firebase/config";
import { useAuth } from "../../contexts/AuthContext";
import { listContacts, removeContact } from "../../lib/contacts";

const STATUS_STYLE = {
  awaiting_upload: "bg-slate-100 text-slate-700",
  sending: "bg-amber-100 text-amber-800",
  send_failed: "bg-red-100 text-red-700",
  sent: "bg-amber-100 text-amber-800",
  partial: "bg-amber-100 text-amber-800",
  complete: "bg-emerald-100 text-emerald-800",
  void: "bg-red-100 text-red-700"
};

const fmtDate = (ts) => {
  const d = ts?.toDate?.();
  if (!d) return "";
  return d.toLocaleDateString("en-NZ", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [docs, setDocs] = useState([]);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "documents"),
      where("ownerUid", "==", user.uid),
      orderBy("createdAt", "desc")
    );
    return onSnapshot(q, (snap) =>
      setDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
  }, [user]);

  const loadContacts = () => {
    if (!user) return;
    listContacts(user.uid)
      .then(setContacts)
      .catch(() => setContacts([]));
  };
  useEffect(loadContacts, [user]);

  const onDeleteContact = async (id) => {
    await removeContact(user.uid, id);
    loadContacts();
  };

  return (
    <div className="mx-auto max-w-3xl p-4">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Documents</h1>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Link to="/edit" className="btn-secondary">
            Edit a PDF
          </Link>
          <Link to="/admin/new" className="btn-primary">
            New document
          </Link>
          <button className="btn-secondary" onClick={logout}>
            Sign out
          </button>
        </div>
      </header>

      <h2 className="mb-2 mt-4 text-lg font-semibold">Documents</h2>
      {docs.length === 0 ? (
        <div className="card text-slate-500">
          No documents yet. Tap "New document" to send one for signing.
        </div>
      ) : (
        <ul className="space-y-3">
          {docs.map((d) => (
            <li key={d.id}>
              <Link
                to={`/admin/documents/${d.id}`}
                className="card block hover:ring-brand-500"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate font-medium">
                      {d.title || "Untitled"}
                    </div>
                    <div className="text-sm text-slate-500">
                      {d.signerCount || 0} signers · {d.mode || "parallel"}
                      {d.createdAt && <> · created {fmtDate(d.createdAt)}</>}
                      {d.completedAt && <> · completed {fmtDate(d.completedAt)}</>}
                    </div>
                  </div>
                  <span
                    className={
                      "whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium " +
                      (STATUS_STYLE[d.status] || STATUS_STYLE.awaiting_upload)
                    }
                  >
                    {d.status || "draft"}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <h2 className="mb-2 mt-8 text-lg font-semibold">Saved signers</h2>
      {contacts.length === 0 ? (
        <div className="card text-sm text-slate-500">
          Signers you add to a document are saved here for next time.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
          {contacts.map((c) => (
            <li
              key={c.id}
              className="flex items-center justify-between px-5 py-3"
            >
              <div>
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-slate-500">
                  {c.email} · {c.phoneE164}
                </div>
              </div>
              <button
                onClick={() => onDeleteContact(c.id)}
                className="text-slate-400 hover:text-red-600"
                aria-label={`Remove ${c.name}`}
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
